import { TokenManager } from '../token-manager';
import { TokenInfo } from '../types';
import { AuthLogger } from '../auth-logger';
import { checkIsDistributor, validateJwtToken } from '../auth-utils';

export interface LoginWithTokenResult {
  success: boolean;
  tokenInfo: TokenInfo | null;
  isDistributor: boolean;
  error?: string;
}

/**
 * LoginWithTokenUseCase - Automatic login with external token
 * Validates and stores token received via postMessage
 */
export class LoginWithTokenUseCase {
  constructor(private readonly tokenManager: TokenManager) {}

  async execute(token: string): Promise<LoginWithTokenResult> {
    try {
      AuthLogger.logInfo('🔐 Attempting automatic login with external token');

      if (!token || typeof token !== 'string') {
        return {
          success: false,
          tokenInfo: null,
          isDistributor: false,
          error: 'Invalid token format'
        };
      }

      // Validate token expiration
      const validation = validateJwtToken(token, 60);
      
      if (!validation.valid) {
        AuthLogger.logError('Received invalid or expired token', new Error(validation.reason));
        console.log('❌ Token validation failed:', validation.reason);
        
        return {
          success: false,
          tokenInfo: null,
          isDistributor: false,
          error: validation.reason || 'Token validation failed'
        };
      }

      const decoded = validation.decoded!;

      // Extract user information from token
      const operatorId = (decoded.OperatorId || decoded.operatorId || decoded.actor)?.toString();
      const distributorId = (decoded.DistributorId || decoded.distributorId)?.toString();
      const accountId = (decoded.AccountId || decoded.accountId)?.toString();

      if (!operatorId) {
        AuthLogger.logError('Token missing operatorId', new Error('Missing operatorId'));
        return {
          success: false,
          tokenInfo: null,
          isDistributor: false,
          error: 'Token missing required fields'
        };
      }

      const tokenInfo: TokenInfo = {
        appToken: token,
        accountToken: token,
        accountId: accountId || 'unknown',
        operatorId: operatorId,
        distributorId: distributorId,
        expiry: new Date(decoded.exp * 1000)
      };

      // Persist validated token to localStorage
      await this.tokenManager.saveCortexSession(token);

      try {
        sessionStorage.setItem('cortex_session', token);
      } catch (error) {
        console.warn('Failed to save to sessionStorage:', error);
      }

      const isDistributor = checkIsDistributor(tokenInfo);

      AuthLogger.logInfo('✅ Automatic login successful', {
        operatorId,
        distributorId,
        isDistributor,
        expiry: tokenInfo.expiry.toISOString()
      });

      return {
        success: true,
        tokenInfo,
        isDistributor
      };
    } catch (error) {
      AuthLogger.logError('Failed to login with token', error);
      
      return {
        success: false,
        tokenInfo: null,
        isDistributor: false,
        error: error instanceof Error ? error.message : 'Failed to process token'
      };
    }
  }
}
